import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Alert, AlertTitle, Box, CircularProgress, List, ListItem, Typography,
} from '@mui/material'
import ErrorIcon from '@mui/icons-material/Error'
import { useForm } from 'react-hook-form'
import { MUIFormFileInput } from '../../../../../@Common/Form/FileInput/FormFileInput'
import { ModalContainer } from '../../../../../Modals/ModalContainer'
import {
  OFFER_IMPORT_FILE_SIZE_ERROR,
  OFFER_IMPORT_MAX_FILE_SIZE_KB,
} from './offerImportConstants'

const validateCsvFile = (file) => (
  file?.name?.toLowerCase().endsWith('.csv') || 'Only CSV files are supported.'
)

function ImportOffersModal({
  onClickSubmit,
  onClickClose,
}) {
  const [processing, setProcessing] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [fileInputKey, setFileInputKey] = useState(0)
  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    watch,
  } = useForm({
    defaultValues: { importFile: null },
    mode: 'onChange',
  })

  const onSubmit = async (data) => {
    setProcessing(true)
    setImportErrors([])

    try {
      const result = await onClickSubmit(data)
      if (result?.errors?.length > 0) {
        setImportErrors(result.errors)
        reset({ importFile: null })
        setFileInputKey((key) => key + 1)
      } else if (!result?.closed) {
        onClickClose()
      }
    } finally {
      setProcessing(false)
    }
  }

  const hasFile = Boolean(watch('importFile'))

  return (
    <ModalContainer
      title="Import Offers"
      maxWidth="sm"
      fullWidth="true"
      processing={false}
      onClose={onClickClose}
      disableClose={processing}
      actions={processing ? {} : {
        primaryAction: {
          label: 'Import',
          onClick: handleSubmit(onSubmit),
          disabled: !hasFile || !isValid,
        },
        secondaryAction: {
          label: 'Cancel',
          onClick: onClickClose,
        },
      }}
    >
      {processing ? (
        <Box sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 6,
        }}
        >
          <Typography sx={{ mb: 2 }}>Your import is processing...</Typography>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography component="div">
            {importErrors.length > 0 ? (
              <Box sx={{
                marginBottom: '24px',
                maxHeight: '400px',
                maxWidth: '1000px',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
              >
                <Alert
                  icon={<ErrorIcon />}
                  severity="error"
                  sx={{
                    '& .MuiAlert-icon': { color: '#d32f2f' },
                    backgroundColor: '#FDEDED',
                    color: '#5F2120',
                    marginTop: '0px',
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 'bold' }}>
                    Please correct the following errors in the file and try again.
                  </AlertTitle>
                  <List sx={{ listStylePosition: 'inside', listStyleType: 'disc' }}>
                    {importErrors.map((error, index) => (
                      <ListItem
                        key={`${index}-${error}`}
                        sx={{ display: 'list-item', overflowWrap: 'anywhere' }}
                      >
                        {error}
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              </Box>
            ) : (
              <>
                <Box>
                  To import offers, make sure your file adheres to the following standards:
                </Box>
                <ul style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  listStyle: 'inside',
                  margin: '8px 0',
                  padding: 0,
                }}
                >
                  <li>File should be a CSV format</li>
                  <li>Use the offer upload template.</li>
                  <li>
                    Optional offer_status column: enter published or unpublished (case-insensitive).
                    Leave it blank to keep existing offer status unchanged; new offers default to unpublished.
                    Omitting the column leaves all statuses unchanged.
                  </li>
                </ul>
              </>
            )}
          </Typography>
          <Box sx={{ my: '20px', mx: '40px' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <MUIFormFileInput
                key={fileInputKey}
                name="importFile"
                control={control}
                fileTypes=".csv"
                fileTypesDescription="CSV files only"
                maxSize={OFFER_IMPORT_MAX_FILE_SIZE_KB}
                maxSizeErrorMessage={OFFER_IMPORT_FILE_SIZE_ERROR}
                processing={processing}
                required
                validateFile={validateCsvFile}
                onChange={() => setImportErrors([])}
                gxid="btnImportOffersBrowseFiles"
              />
            </form>
          </Box>
        </>
      )}
    </ModalContainer>
  )
}

ImportOffersModal.propTypes = {
  onClickSubmit: PropTypes.func.isRequired,
  onClickClose: PropTypes.func.isRequired,
}

export default ImportOffersModal
